import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import "./dash.css";
import debounce from "lodash.debounce";
import DashboardNavbar from "./DashboardNavbar";
import JSZip from "jszip";
import { useLoading } from "./LoadingContext";
import Pagination from "../Pagination";

function Dashboard() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [buttonData, setButtonData] = useState([]);
  const [showButtonData, setShowButtonData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [selectedJD, setSelectedJD] = useState([]);
  const [files, setFiles] = useState([]);
  const [jdFiles, setJdFiles] = useState([]);
  const jdFileInputRef = useRef(null);
  // const [directoryPath, setDirectoryPath] = useState("");
  const fileInputRef = useRef(null); // Create a reference for the file input
  const { loading, setLoading } = useLoading();
  const [sortField, setSortField] = useState(""); // Sorting field
  const [sortOrder, setSortOrder] = useState("asc"); // Sorting order: "asc" or "desc"

  const debouncedSearch = debounce((searchQuery) => {
    setSearchQuery(searchQuery);
  }, 500);

  const handleSearch = (e) => {
    debouncedSearch(e.target.value);
    setSearch(e.target.value);
  };

  // const handleDirectoryChange = (e) => {
  //   setDirectoryPath(e.target.value);
  // };

  const clearFiles = () => {
    setFiles([]); // Clear the files state
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset the file input value
    }
  };

  const handleFileChange = (e) => {
    setFiles([...e.target.files]); // Store the selected files
  };

  const handleAdd = (event) => {
    event.preventDefault();
    navigate("/jdform");
  };

  useEffect(() => {
    const fetchData = async () => {
      const storedClient = localStorage.getItem("client");
      const storedUser = localStorage.getItem("user_id");

      // Convert stored values to numbers
      const client_id = storedClient ? Number(storedClient) : null;
      const user_id = storedUser ? Number(storedUser) : null;

      // Debugging: Log retrieved values
      console.log("Fetching data with:", { client_id, user_id });

      // Ensure client_id and user_id are valid before making the request
      if (!client_id || !user_id) {
        console.error("Missing client_id or user_id. Please log in again.");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/job-descriptions/fetch/?client_id=${client_id}&user_id=${user_id}`
        );
        const activeData = response.data.data.filter((item) => item.is_active);
        setData(activeData);
      } catch (error) {
        console.log(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchButtonData = async () => {
      const storedClient = localStorage.getItem("client");
      const storedUser = localStorage.getItem("user_id");

      // Convert stored values to numbers
      const client_id = storedClient ? Number(storedClient) : null;
      const user_id = storedUser ? Number(storedUser) : null;

      // Debugging: Log retrieved values
      console.log("Fetching data with:", { client_id, user_id });

      // Ensure client_id and user_id are valid before making the request
      if (!client_id || !user_id) {
        console.error("Missing client_id or user_id. Please log in again.");
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/job-descriptions/list-deactivated/?client_id=${client_id}&user_id=${user_id}`
        );
        setButtonData(response.data.data);
      } catch (error) {
        console.log(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchButtonData();
  }, []);

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to deactivate this JD?"
    );
    if (isConfirmed) {
      try {
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/api/job-descriptions/${id}/deactivate/`,
          {
            client_id: 2, // Hardcoded client_id
            user_id: 6, // Hardcoded user_id
          }
        );
        setData(data.filter((item) => item.id !== id)); // Update state to remove deactivated JD
        window.location.reload();
      } catch (error) {
        console.log(error.message); // Handle error
      }
    }
  };
  // const handleSelect = (item) => {
  //     setSelectedJD(item.id);
  //   };

  const handleSelect = (item) => {
    setSelectedJD((prevSelected) => {
      if (prevSelected.includes(item.id)) {
        // If already selected, remove it
        return prevSelected.filter((id) => id !== item.id);
      } else {
        // Otherwise, add it
        return [...prevSelected, item.id];
      }
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Retrieve user_id from localStorage
    const storedUser = localStorage.getItem("user_id");
    const user_id = storedUser ? Number(storedUser) : null;

    // Debugging: Log retrieved values
    console.log("Uploading with:", { user_id });

    if (!user_id) {
      alert("User ID is missing. Please log in again.");
      return;
    }

    if (files.length === 0 || selectedJD.length === 0) {
      alert(
        "Please select files and at least one job description before submitting."
      );
      return;
    }

    setLoading(true);

    try {
      const zip = new JSZip();
      files.forEach((file) => zip.file(file.name, file));
      const zipBlob = await zip.generateAsync({ type: "blob" });

      const formData = new FormData();
      formData.append("directory", new File([zipBlob], "files.zip"));
      formData.append("job_description_ids", JSON.stringify(selectedJD)); // Pass the list of JD IDs as a JSON string
      formData.append("user_id", user_id); // Include user_id in request

      // Debugging: Check if formData contains user_id
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/screener/upload-directory/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (
        (response.status === 200 || response.status === 201) &&
        response.data?.is_success
      ) {
        const result = response.data;
        const task_id = result.task_id;

        console.log("✅ Upload succeeded:", result);
        navigate("/matched_results", {
          state: {
            taskId: task_id,
            matchedData: result.data,
          },
        });
      } else {
        console.error(
          "❌ Upload failed block triggered:",
          response.status,
          response.data
        );
      }
    } catch (error) {
      console.error("An error occurred while uploading:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    // Retrieve client ID and user ID from localStorage

    const storedUser = localStorage.getItem("user_id");

    const user_id = storedUser ? Number(storedUser) : null;

    // Debugging: Log retrieved values
    console.log("Submitting JD with:", { user_id });

    if (!user_id) {
      alert("Client ID or User ID is missing. Please log in again.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/job-descriptions/download?user_id=${user_id}`,
        {
          method: "GET",
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "JD_Data.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error("Download failed");
      }
    } catch (error) {
      console.error("An error occurred during download", error);
    }
  };

  if (loading) {
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div>
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text-center text-dark">
          &nbsp;Your AIRecruiter is Processing....
        </h3>
      </div>
    );
  }

  const handleUploadJD = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      alert("Please select at least one JD file to upload.");
      return;
    }

    const storedClient = localStorage.getItem("client");
    const createdBy = localStorage.getItem("user_id");

    const clientId =
      storedClient && !isNaN(storedClient) ? Number(storedClient) : null;
    const createdById =
      createdBy && !isNaN(createdBy) ? Number(createdBy) : null;

    if (!clientId || !createdById) {
      alert("Client ID or User ID is missing. Please log in again.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("file", file);
      });
      formData.append("client_id", clientId);
      formData.append("created_by_id", createdById); // 🔁 updated key

      // ✅ Log what's being sent (add here)
      for (let pair of formData.entries()) {
        console.log(pair[0] + ": ", pair[1]);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/job-descriptions/extract/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        alert("JD uploaded and extracted successfully!");
        window.location.reload();
      } else {
        alert("Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading JD:", error);
      alert("Something went wrong while uploading JD.");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return "▲▼"; // Display both arrows by default
    return sortOrder === "asc" ? "▲" : "▼"; // Show the active arrow based on sorting order
  };

  const sortedItems = [...data].sort((a, b) => {
    if (sortField) {
      if (sortOrder === "asc") {
        return a[sortField] > b[sortField] ? 1 : -1;
      } else {
        return a[sortField] < b[sortField] ? 1 : -1;
      }
    }
    return 0;
  });

  const filteredItems = showButtonData
    ? buttonData.filter(
        (item) =>
          (item.id?.toString() || "").includes(searchQuery) ||
          (item.role?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.description?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.mandatory_skills?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.optional_skills?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.total_experience?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.relevant_experience?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.education?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.location?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.offered_CTC?.toString() || "").includes(searchQuery)
      )
    : sortedItems.filter(
        (item) =>
          (item.id?.toString() || "").includes(searchQuery) ||
          (item.role?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.description?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.mandatory_skills?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.optional_skills?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.total_experience?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.relevant_experience?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.education?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.location?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.offered_CTC?.toString() || "").includes(searchQuery)
      );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  return (
    <>
      <DashboardNavbar />
      <div className="container py-3 main-content">
        <div className="p-2 mb-1 rounded">
          <h3 className="text-center  font_header">
            Job Description Configurator
          </h3>
          <div className="row align-items-center mt-1 mb-4">
            <div className="col-12 col-lg-4 mb-2 mb-lg-0">
              <div className="search">
                <form className="form-inline fw-bold">
                  <div className="col-lg-8 col-md-5">
                    <label htmlFor="search" className="">
                      Search:
                    </label>
                    <input
                      className="form-control mt-1 bg-transparent border-dark"
                      type="search"
                      title="Search"
                      aria-label="Search"
                      onChange={handleSearch}
                      value={search}
                    />
                  </div>
                </form>
              </div>
              {/* Directory Path Input */}
              <div className="form-group mt-1 col-lg-8 col-md-5">
                <label htmlFor="directory_path" className="fw-bold">
                  Upload Files:
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.zip"
                  id="directory_path"
                  name="directory_path"
                  className="form-control mt-1 bg-transparent border-dark"
                  // value={directoryPath}
                  // onChange={handleDirectoryChange}
                  onChange={handleFileChange}
                  title="Upload files"
                  ref={fileInputRef}
                />
                {files.length > 0 && (
                  <button
                    className="btn btn-outline-danger mt-1"
                    title="Clear Files"
                    onClick={clearFiles}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-12 col-lg-8 text-lg-end mt-3">
              <button
                className="btn btn-sm btn-outline-info mb-2 mx-2"
                title="Submit JD & Files"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Uploading..." : "Submit"}
              </button>
              <button
                className="btn btn-sm btn-outline-info mb-2"
                title="Add JD"
                onClick={handleAdd}
              >
                Add JD
              </button>

              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx"
                ref={jdFileInputRef}
                onChange={(e) => {
                  const selected = [...e.target.files];
                  if (selected.length > 0) {
                    setJdFiles(selected);
                    handleUploadJD(selected);
                  }
                }}
                style={{ display: "none" }}
              />
              <button
                className="btn btn-sm btn-outline-info mb-2 mx-2"
                title="Upload JD"
                onClick={() => jdFileInputRef.current.click()} // open file dialog
              >
                Upload JD
              </button>

              <button
                className="btn btn-sm btn-outline-info mb-2"
                onClick={() => setShowButtonData(!showButtonData)}
              >
                {showButtonData ? "Show Active JD" : "Show Deactivated JD"}
              </button>

              <button
                className="btn btn-sm btn-outline-info mb-2 mx-2"
                title="Download JD Data"
                onClick={handleDownload}
              >
                Download
              </button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="table-responsive  text-center table-wrapper">
            <table
              className="table table-hover table-bordered border-dark"
              style={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "800px",
              }}
            >
              <thead className="tb_header border-dark">
                <tr>
                  <th
                    className={sortField === "id" ? "sorted" : ""}
                    onClick={() => handleSort("id")}
                    style={{
                      width: "100px",
                      cursor: "pointer",
                    }}
                  >
                    ID
                    {getSortArrow("id")}
                  </th>
                  <th
                    onClick={() => handleSort("role")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Role
                    {getSortArrow("role")}
                  </th>
                  <th
                    onClick={() => handleSort("description")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Description
                    {getSortArrow("description")}
                  </th>
                  <th
                    onClick={() => handleSort("mandatory_skills")}
                    style={{ width: "400px", cursor: "pointer" }}
                  >
                    Mandatory&nbsp;Skills
                    {getSortArrow("mandatory_skills")}
                  </th>
                  <th
                    onClick={() => handleSort("optional_skills")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Optional&nbsp;Skills
                    {getSortArrow("optional_skills")}
                  </th>
                  <th
                    onClick={() => handleSort("total_experience")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Total&nbsp;Experience
                    {getSortArrow("total_experience")}
                  </th>
                  <th
                    onClick={() => handleSort("relevant_experience")}
                    style={{ width: "250px", cursor: "pointer" }}
                  >
                    Relevant&nbsp;Experience
                    {getSortArrow("relevant_experience")}
                  </th>
                  <th
                    onClick={() => handleSort("education")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Education
                    {getSortArrow("education")}
                  </th>
                  <th
                    onClick={() => handleSort("location")}
                    style={{ width: "250px", cursor: "pointer" }}
                  >
                    Location
                    {getSortArrow("location")}
                  </th>
                  <th
                    onClick={() => handleSort("ectc")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Offered&nbsp;CTC
                    {getSortArrow("etc")}
                  </th>
                  <th
                    onClick={() => handleSort("ectc")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    JD's Age
                    {getSortArrow("etc")}
                  </th>
                  <th style={{ width: "200px" }}>Weightage (%)</th>{" "}
                  {/* New Column */}
                  {!showButtonData && (
                    <th style={{ width: "150px" }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="text-center table-light border-dark">
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.role}</td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.description}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.mandatory_skills}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.optional_skills}
                      </td>
                      <td>{item.total_experience}</td>
                      <td>{item.relevant_experience}</td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.education}
                      </td>
                      <td>{item.location}</td>
                      <td>{item.offered_CTC}</td>
                      <td>{item.jd_age}</td>

                      {/* ✅ Displaying JD Weightages */}
                      <td style={{ whiteSpace: "pre-line" }}>
                        <strong>Mandatory Skills:</strong>{" "}
                        {item.mandatory_skills_weight || 0}%{"\n"}
                        <strong>Optional Skills:</strong>{" "}
                        {item.optional_skills_weight || 0}%{"\n"}
                        <strong>Relevant Exp:</strong>{" "}
                        {item.relevant_experience_weight || 0}%{"\n"}
                        <strong>Education:</strong> {item.education_weight || 0}
                        %{"\n"}
                        <strong>Location:</strong> {item.location_weight || 0}%
                        {"\n"}
                      </td>

                      {!showButtonData && (
                        <td>
                          {/* <input
                            className="form-check-input me-1 border-dark"
                            type="radio"
                            name="selectedJD"
                            title="Select JD"
                            id={`radioNoLabel${item.id}`}
                            value={item.id}
                            onChange={() => handleSelect(item)}
                            aria-label="..."
                          /> */}
                          <input
                            className="form-check-input me-1 border-dark"
                            type="checkbox" // Changed from "radio" to "checkbox"
                            name={`selectedJD${item.id}`}
                            title="Select JD"
                            id={`checkboxNoLabel${item.id}`}
                            value={item.id}
                            onChange={() => handleSelect(item)}
                            checked={selectedJD.includes(item.id)} // Ensure the checkbox is checked if selected
                            aria-label="..."
                          />

                          <Link
                            to={`/add_jd/${item.id}`}
                            className="copy"
                            title="Copy this JD"
                            data-toggle="tooltip"
                          >
                            <i className="fa fa-copy ms-1 me-1"></i>
                          </Link>
                          <Link
                            to={`/edit/${item.id}`}
                            className="edit"
                            title="Edit"
                            data-toggle="tooltip"
                          >
                            <i className="fa fa-edit ms-1 me-1"></i>
                          </Link>
                          <Link
                            to="#"
                            className="delete"
                            title="Deactivate JD"
                            data-toggle="tooltip"
                            onClick={() => handleDelete(item.id)}
                          >
                            <i className="fa fa-trash text-danger ms-1"></i>
                          </Link>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="15" className="text-start">
                      <h4>No data available</h4>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Pagination Code */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        paginate={(page) => setCurrentPage(page)}
      />
    </>
  );
}

export default Dashboard;
